import { isBefore, isAfter, parse, format } from 'date-fns';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import buffer from '@turf/buffer';

const fetchStormAdvisories = (stormId) => {
  return fetch(
    `https://coast.noaa.gov/hurricanes/api/v1/search/stormAdvisoriesGeoJSONByID?stormID=${stormId}`
  ).then((response) => response.json());
};

export const fetchAdvisories = (storms, currGeometry) => {
  const aoi = buffer(currGeometry, 0.5, { unit: 'miles' });
  return Promise.all(
    storms.map(async (storm) => {
      const advisory = await fetchStormAdvisories(storm.stormID);

      const minMaxs = advisory.data.storm.features.reduce(
        (acc, feat) => {
          if (booleanPointInPolygon(feat, aoi)) {
            const date = parse(
              feat.properties.date,
              'MM/dd/yyyy HH',
              new Date()
            );
            
            if (acc.aoiDateRange.min === null || isBefore(date, acc.aoiDateRange.min)) {
              acc.aoiDateRange.min = date;
            }
            if (acc.aoiDateRange.max === null || isAfter(date, acc.aoiDateRange.max)) {
              acc.aoiDateRange.max = date;
            }
            
            const windSpeed = feat.properties.windSpeed;
            if (acc.aoiWindSpeed.min === null || windSpeed < acc.aoiWindSpeed.min) {
              acc.aoiWindSpeed.min = windSpeed;
            }
            if (acc.aoiWindSpeed.max || windSpeed > acc.aoiWindSpeed.max) {
              acc.aoiWindSpeed.max = windSpeed;
            }
          }

          return acc;
        },
        {
          aoiDateRange: {
            min: null,
            max: null,
          },
          aoiWindSpeed: { min: null, max: null },
        }
      );

      let aoiDateRange = null;
      if (minMaxs.aoiDateRange.min !== null) {
        const start = format(minMaxs.aoiDateRange.min, 'MM/dd');
        const end = format(minMaxs.aoiDateRange.max, 'MM/dd');
        aoiDateRange = start === end ? start : `${start}-${end}`;
      }

      let aoiWindSpeed = null;
      if (minMaxs.aoiWindSpeed.min !== null) {
        const min = minMaxs.aoiWindSpeed.min;
        const max = minMaxs.aoiWindSpeed.max;
        aoiWindSpeed = min === max ? min : `${min} - ${max}`;
      }

      return {
        id: storm.stormID,
        name: storm.name,
        stormCategory: storm.category,
        fullDateRange: storm.dateRange,
        url: storm.detailsURL,
        aoiCategories: storm.categoriesInAOI,
        aoiDateRange,
        aoiWindSpeed,
      };
    })
  );
};

const convertArrayOfCoords = (arr) => {
  const str = arr[0].reduce((acc, pair, i) => {
    acc += `${pair[0]} ${pair[1]}`;
    if (i !== arr[0].length - 1) {
      acc += ', ';
    }
    return acc;
  }, '');
  return `((${str}))`;
};

const convertObjToString = (obj) => {
  let polygonString = obj.type.toUpperCase();
  if (polygonString === 'MULTIPOLYGON') {
    polygonString += '(';
    obj.coordinates.forEach((shp, i) => {
      polygonString += convertArrayOfCoords(shp);

      if (i !== obj.coordinates.length - 1) {
        polygonString += ', ';
      }
    });
    polygonString += ')';
  } else {
    polygonString += convertArrayOfCoords(obj.coordinates);
  }

  return polygonString;
};

const divideStorms = (storms, lastFullYear) => {
  const hurricanes = [...Array(lastFullYear - 1842 + 1)].map(() => []);
  const tropStorms = [...Array(lastFullYear - 1842 + 1)].map(() => []);

  storms.forEach((storm) => {
    const idx = Math.min(...storm.yearsInAOI) - 1842;
    const catLetters = storm.categoriesInAOI.join('');

    if (catLetters.includes('T')) {
      tropStorms[idx].push(storm);
    }

    if (catLetters.includes('H')) {
      hurricanes[idx].push(storm);
    }
  });

  return { hurricanes, tropStorms };
};

const fetchStorms = (polygon) => {
  return fetch('https://coast.noaa.gov/hurricanes/api/v1/search/stormsByAOI', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      aoi: `SRID=4326;${polygon}`,
      buffer: 111120,    // buffer is in meters, 111,120m is equal to 60 nm
    }),
  }).then((response) => response.json());
};

export const fetchFromCoastAPI = async (polygonObj, lastFullYear) => {
  const polygonString = convertObjToString(polygonObj);
  const aoiStorms = await fetchStorms(polygonString);
  return divideStorms(aoiStorms.data.storms, lastFullYear);
};